import React, { useState } from 'react';
import { AlertOctagonIcon, CameraIcon, CheckCircle2Icon, ScanLineIcon } from 'lucide-react';
import { toast } from 'sonner';
import type { CustodyCase } from '../../types/case';
import type { DemoUser } from '../../types/config';
import type { FieldScreen } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { MobileHeader } from '../../components/mobile/MobileHeader';
import { ScannerView } from '../../components/mobile/ScannerView';
import { categoryLabel } from '../../utils/caseStatus';
import { bn, fmtDateTime, nowIso } from '../../utils/bn';
const STATION = 'তেজগাঁও ডাম্পিং স্টেশন';
const CONDITIONS = ['ভালো', 'মোটামুটি', 'খারাপ', 'চলাচলের অনুপযোগী'];
export function StationReceive({
  user,
  go
}: {
  user: DemoUser;
  go: (s: FieldScreen) => void;
}) {
  const {
    cases,
    config,
    updateCase,
    getCase
  } = useStore();
  const [pcr, setPcr] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  const [scanError, setScanError] = useState('');
  const [done, setDone] = useState(false);
  const [condition, setCondition] = useState('ভালো');
  const [damage, setDamage] = useState('');
  const [photo, setPhoto] = useState(false);
  const [zone, setZone] = useState('B');
  const [slot, setSlot] = useState('B-12');
  const station = config.stations.find(s => s.name === STATION);
  const incoming = cases.filter(c => c.stage === 'in_transit');
  const voided = cases.flatMap(c => c.barcode.reprints.map(r => ({
    serial: r.oldSerial,
    pcr: c.pcr
  })));
  const c: CustodyCase | undefined = pcr ? getCase(pcr) : undefined;
  const scan = (code: string) => {
    setScanError('');
    const v = code.trim().toUpperCase();
    const void_ = voided.find(x => x.serial === v);
    if (void_) {
      setScanError(`স্টিকার ${v} বাতিল করা হয়েছে এবং আর বৈধ নয়। যানবাহনে সংযুক্ত সর্বশেষ স্টিকার স্ক্যান করুন।`);
      return;
    }
    const found = cases.find(x => x.barcode.serial === v || x.pcr === v);
    if (!found) {
      setScanError('এই বারকোডের সাথে কোনো PCR পাওয়া যায়নি।');
      return;
    }
    if (found.stage === 'at_station' || found.stage === 'released') {
      setScanError(`${found.pcr} ইতোমধ্যে স্টেশনে গৃহীত হয়েছে (${found.station?.station ?? ''}, স্লট ${found.station?.slot ?? ''})।`);
      return;
    }
    if (found.stage === 'registered') {
      setScanError(`${found.pcr}-এর পিকআপ এখনো সম্পন্ন হয়নি।`);
      return;
    }
    const n = cases.filter(x => x.station?.zone === zone).length + 13;
    setSlot(`${zone}-${String(n).padStart(2, '0')}`);
    setPcr(found.pcr);
  };
  const confirm = () => {
    if (!c) return;
    updateCase(c.pcr, x => ({
      ...x,
      stage: 'at_station',
      station: {
        station: STATION,
        zone,
        slot,
        receivedAt: nowIso(),
        condition,
        damage: damage || 'দৃশ্যমান ক্ষতি নেই',
        officer: user.name
      }
    }), {
      key: 'received',
      actor: user.name,
      role: user.title,
      action: `${STATION}-এ গ্রহণ — জোন ${zone}, স্লট ${slot}`,
      note: photo ? 'গ্রহণের ছবি সংযুক্ত' : undefined
    });
    setDone(true);
    toast.success('যানবাহন গ্রহণ নিশ্চিত হয়েছে। স্টেশন ইনভেন্টরি হালনাগাদ হয়েছে।');
  };
  if (done && c) {
    return <>
        <MobileHeader appName="ডিএমপি যানবাহন হেফাজত" title="ডাম্পিং স্টেশনে যানবাহন গ্রহণ" onBack={() => go({
        name: 'home'
      })} data-id="element-1613" data-mp="1lsefggx508b9" />
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center" data-id="element-1614" data-mp="17tyaauexapv8">
          <CheckCircle2Icon className="h-16 w-16 text-success" data-id="element-1615" data-mp="1cr6pqn8osfss" />
          <p className="mt-3 text-lg font-bold text-ink" data-id="element-1616" data-mp="20hkcpzcezogi">যানবাহন গ্রহণ সম্পন্ন</p>
          <p className="text-sm text-ink-muted" data-id="element-1617" data-mp="25g3xhwf9uu7a">{c.pcr} · {c.regNo}</p>
          <div className="mt-4 rounded-xl bg-navy-50 px-6 py-3" data-id="element-1618" data-mp="25hf5k8fr2o47"><p className="text-xs text-ink-muted" data-id="element-1619" data-mp="1qpwos8f2gpjs">পার্কিং অবস্থান</p><p className="text-2xl font-bold text-navy" data-id="element-1620" data-mp="1vpj36l6msbu6">জোন {zone} · {slot}</p></div>
          <div className="mt-6 flex w-full gap-2" data-id="element-1621" data-mp="1vng4lcrexadu">
            <button className="btn btn-outline flex-1" onClick={() => go({
            name: 'records'
          })} data-id="element-1622" data-mp="2jec12n6tctrf">ইনভেন্টরি</button>
            <button className="btn btn-primary flex-1" onClick={() => {
            setDone(false);
            setPcr(null);
          }} data-id="element-1623" data-mp="2o7oe0024rc4c">পরবর্তী স্ক্যান</button>
          </div>
        </div>
      </>;
  }
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন হেফাজত" title="ডাম্পিং স্টেশনে যানবাহন গ্রহণ" onBack={() => c ? setPcr(null) : go({
      name: 'home'
    })} data-id="element-1624" data-mp="vhw5ucxgsddm" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3" data-id="element-1625" data-mp="lkhq6fjd7smd">
        {!c ? <>
            <ScannerView mode="barcode" hint="যানবাহনের বারকোড স্টিকার ফ্রেমের মধ্যে রাখুন" data-id="element-1626" data-mp="1j8rs6uj5igdh" />
            {scanError && <p className="flex items-start gap-2 rounded-lg bg-danger-50 p-3 text-xs text-danger-700" role="alert" data-id="element-1627" data-mp="1e8x04nnn3kqu"><AlertOctagonIcon className="h-4 w-4 shrink-0" data-id="element-1628" data-mp="19hop5n6nvrbk" />{scanError}</p>}
            <div className="card p-3" data-id="element-1629" data-mp="14jn7n6ovw027">
              <p className="text-sm font-bold text-ink" data-id="element-1630" data-mp="14ikhj0zdy1nc">ডেমো স্ক্যান — আগত যানবাহন</p>
              {incoming.length === 0 && <p className="mt-2 text-xs text-ink-muted" data-id="element-1631" data-mp="220c6crijem7d">কোনো পরিবহনাধীন যানবাহন নেই। কর্মকর্তা অ্যাপে রেকার অপারেটর হিসেবে পিকআপ সম্পন্ন করুন।</p>}
              <div className="mt-2 space-y-1.5" data-id="element-1632" data-mp="24jflqxxawmb6">
                {incoming.map(x => <button key={x.pcr} onClick={() => scan(x.barcode.serial ?? x.pcr)} className="flex w-full items-center justify-between rounded-lg border border-line px-3 py-2 text-left hover:bg-navy-50" data-id="element-1633" data-mp="29iafguwt3va2">
                    <span data-id="element-1634" data-mp="2jg6p1ia3ii62"><span className="block font-mono text-xs font-bold text-navy" data-id="element-1635" data-mp="2jgzlnqfcbrah">{x.barcode.serial}</span><span className="text-xs text-ink-muted" data-id="element-1636" data-mp="2ni1pylo46tjb">{x.pcr} · {x.regNo}</span></span>
                    <ScanLineIcon className="h-4 w-4 text-navy" data-id="element-1637" data-mp="2sh4rhw4vyhmj" />
                  </button>)}
                {voided.slice(0, 1).map(v => <button key={v.serial} onClick={() => scan(v.serial)} className="flex w-full items-center justify-between rounded-lg border border-danger/30 px-3 py-2 text-left hover:bg-danger-50" data-id="element-1638" data-mp="2x8d22vhyc91a">
                    <span data-id="element-1639" data-mp="377lq77q4ytfq"><span className="block font-mono text-xs font-bold text-danger" data-id="element-1640" data-mp="377vjb46h9gpn">{v.serial}</span><span className="text-xs text-ink-muted" data-id="element-1641" data-mp="3b8fj6fifqyfh">বাতিলকৃত স্টিকার (ব্যতিক্রম ডেমো)</span></span>
                    <ScanLineIcon className="h-4 w-4 text-danger" data-id="element-1642" data-mp="1c3dza50mmq89" />
                  </button>)}
              </div>
            </div>
            <div className="flex gap-2" data-id="element-1643" data-mp="1kuxuih622hyh">
              <input className="input font-mono" placeholder="STK-XXXXXX বা PCR নম্বর" value={manual} onChange={e => setManual(e.target.value)} data-id="element-1644" data-mp="1kvzgvhsiuqvi" />
              <button className="btn btn-primary" onClick={() => scan(manual)} data-id="element-1645" data-mp="1utdjoelivh5c">খুঁজুন</button>
            </div>
          </> : <>
            <div className="card overflow-hidden" data-id="element-1646" data-mp="1pthjyrjuybvl">
              <img src={c.photo} alt={`${c.regNo} যানবাহনের ছবি`} className="h-36 w-full object-cover" data-id="element-1647" data-mp="23ts99q5irien" />
              <dl className="divide-y divide-line text-sm" data-id="element-1648" data-mp="1zsq9973s32bm">
                {[['PCR রেফারেন্স', c.pcr], ['যানবাহন', `${c.regNo} · ${categoryLabel(c.category, config)}`], ['ব্র্যান্ড / রং', `${c.brand} ${c.model} · ${c.color}`], ['পূর্ববর্তী আটক স্থান', c.road], ['নির্ধারিত রেকার', `${c.wreckerId} · ${c.operator}`], ['গ্রহণের তারিখ/সময়', fmtDateTime(nowIso())], ['স্টেশন', STATION]].map(([k, v]) => <div key={k} className="flex justify-between gap-3 px-4 py-2" data-id="element-1649" data-mp="3hpk2yvxoa32d"><dt className="text-ink-muted" data-id="element-1650" data-mp="3mod8o26pwprz">{k}</dt><dd className="text-right font-semibold" data-id="element-1651" data-mp="3mnbwfudytx8o">{v}</dd></div>)}
              </dl>
            </div>
            <div data-id="element-1652" data-mp="37u8o8dm9kvum">
              <label className="label" data-id="element-1653" data-mp="3cqqtdeliaw7h">গাড়ির বর্তমান অবস্থা</label>
              <div className="grid grid-cols-2 gap-1.5" data-id="element-1654" data-mp="3cow4xqb10lyn">
                {CONDITIONS.map(x => <button key={x} onClick={() => setCondition(x)} className={`rounded-lg border px-2 py-2 text-xs font-semibold ${condition === x ? 'border-navy bg-navy text-white' : 'border-line bg-white'}`} data-id="element-1655" data-mp="2ytafxj4sm268">{x}</button>)}
              </div>
            </div>
            <div data-id="element-1656" data-mp="11yfii1pji6q7"><label className="label" htmlFor="dmg" data-id="element-1657" data-mp="11yfjo51a0crs">দৃশ্যমান ক্ষতির বিবরণ</label><textarea id="dmg" className="textarea" rows={2} value={damage} onChange={e => setDamage(e.target.value)} placeholder="যেমন: সামনের বাম্পারে হালকা দাগ" data-id="element-1658" data-mp="2ajmo8lcm10tr" /></div>
            <div data-id="element-1659" data-mp="20tbict9d8hk8">
              <label className="label" data-id="element-1660" data-mp="20ss0tdnmnv6q">গাড়ি গ্রহণের ছবি</label>
              <button onClick={() => setPhoto(!photo)} className="relative flex h-24 w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-line bg-canvas text-xs text-ink-muted" data-id="element-1661" data-mp="20r769o1bcc8v">
                {photo ? <img src={c.photo} alt="গ্রহণের ছবি" className="absolute inset-0 h-full w-full object-cover" data-id="element-1662" data-mp="1mt93lfwcu497" /> : <span className="flex flex-col items-center" data-id="element-1663" data-mp="1hu5wzr0q7pz4"><CameraIcon className="mb-1 h-5 w-5" data-id="element-1664" data-mp="1hv7jcrn6zyw5" />ছবি তুলুন (সিমুলেটেড)</span>}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2.5" data-id="element-1665" data-mp="1dvgni9it6skr">
              <div data-id="element-1666" data-mp="3c7ozlf9m5yra">
                <label className="label" htmlFor="zone" data-id="element-1667" data-mp="3c7xh5kgols8t">পার্কিং জোন</label>
                <select id="zone" className="input" value={zone} onChange={e => {
              setZone(e.target.value);
              setSlot(`${e.target.value}-${slot.split('-')[1] ?? '01'}`);
            }} data-id="element-1668" data-mp="3886jqxwngdvb">
                  {station?.zones.map(z => <option key={z} data-id="element-1669" data-mp="yxfatrfh2njc">{z}</option>)}
                </select>
              </div>
              <div data-id="element-1670" data-mp="13q8d88ngxjs9"><label className="label" htmlFor="slot" data-id="element-1671" data-mp="13q8dm9rdrlss">পার্কিং স্লট নম্বর</label><input id="slot" className="input font-mono" value={slot} onChange={e => setSlot(e.target.value)} data-id="element-1672" data-mp="17qiuwikmhymy" /></div>
            </div>
            <div data-id="element-1673" data-mp="b1tbgntckxyv"><label className="label" data-id="element-1674" data-mp="b1t9ii9senw8">গ্রহণকারী কর্মকর্তার আইডি</label><div className="input flex items-center font-mono" data-id="element-1675" data-mp="fzba9ldnhwt5">{user.staffId} · {user.name}</div></div>
            <p className="text-[11px] text-ink-soft" data-id="element-1676" data-mp="k1g5gojqz1hw">স্টেশনের ধারণক্ষমতা: {bn(station?.capacity ?? 0)} · জোন: {station?.zones.join(', ')}</p>
          </>}
      </div>
      {c && <div className="shrink-0 border-t border-line bg-white p-3" data-id="element-1677" data-mp="1vgd32mtzyz0v">
          <button className="btn btn-green btn-lg w-full" onClick={confirm} data-id="element-1678" data-mp="1vifth7z2au64">যানবাহন গ্রহণ নিশ্চিত করুন</button>
        </div>}
    </>;
}